import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import AdminHeader from "../header/admin_header";
import AdminSidebar from "../sidebar/admin_sidebar";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config.json";

/* ─────────────────────────────────────────────────────────
   CONSTANTS & HELPERS
───────────────────────────────────────────────────────── */
const ACCENT = "#3f88a5";

const STATUS_CFG = {
  PENDING: { label: "Pending", dotColor: "#f59e0b", textColor: "#92400e", bg: "#fffbeb", border: "#fde68a" },
  APPROVED: { label: "Approved", dotColor: "#10b981", textColor: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" },
  REJECTED: { label: "Rejected", dotColor: "#ef4444", textColor: "#991b1b", bg: "#fef2f2", border: "#fecaca" },
};

const TYPE_CFG = {
  KPI: { color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe" },
  TAG: { color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
  UNIT: { color: "#047857", bg: "#ecfdf5", border: "#a7f3d0" },
};

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/* ─────────────────────────────────────────────────────────
   BADGE COMPONENTS
───────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.PENDING;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
      textTransform: "uppercase", whiteSpace: "nowrap",
      background: c.bg, color: c.textColor, border: `1.5px solid ${c.border}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dotColor, flexShrink: 0 }} />
      {c.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const c = TYPE_CFG[type] || { color: "#374151", bg: "#f9fafb", border: "#e5e7eb" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "3px 9px", borderRadius: 6,
      fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em",
      textTransform: "uppercase", whiteSpace: "nowrap",
      background: c.bg, color: c.color, border: `1.5px solid ${c.border}`,
    }}>
      {type}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────────────────── */
const StatCard = ({ label, value, valueColor, bg, border }) => (
  <div style={{
    flex: 1, minWidth: 140,
    background: bg, border: `1.5px solid ${border}`,
    borderRadius: 12, padding: "16px 20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  }}>
    <div style={{ fontSize: 28, fontWeight: 800, color: valueColor, lineHeight: 1, letterSpacing: "-0.03em" }}>{value}</div>
    <div style={{ fontSize: 12, fontWeight: 600, color: valueColor, marginTop: 5, opacity: 0.75 }}>{label}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   SELECT COMPONENT  (styled native select)
───────────────────────────────────────────────────────── */
const Select = ({ value, onChange, children, minWidth = 150 }) => (
  <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 38, paddingLeft: 12, paddingRight: 34,
        border: "1.5px solid #d1d5db", borderRadius: 8,
        fontSize: 13, color: "#374151", background: "#fff",
        outline: "none", cursor: "pointer", appearance: "none",
        fontFamily: "inherit", minWidth, boxSizing: "border-box",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      {children}
    </select>
    <span style={{ position: "absolute", right: 10, pointerEvents: "none", fontSize: 10, color: "#9ca3af" }}>▼</span>
  </div>
);

const ApproveModal = ({ item, onConfirm, onClose, loading }) => {
  const [selectedKpiId, setSelectedKpiId] = useState("");
  const [availableKpis, setAvailableKpis] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [availableUnits, setAvailableUnits] = useState([]);
  const [fetchingUnits, setFetchingUnits] = useState(false);

  useEffect(() => {
    if (item) {
      if (item.kpi_id) {
        setSelectedKpiId(item.kpi_id);
      } else {
        setSelectedKpiId("");
      }

      const fetchKpis = async () => {
        setFetching(true);
        try {
          // Pointing to your specific route
          const { isSuccess, data } = await apiCall(
            `${config.ADMIN_API_URL}getReportingQuestionnew`,
            {},
            { user_type_code: "super_admin" },
            "POST"
          );

          if (isSuccess && Array.isArray(data.data)) {
            setAvailableKpis(data.data);
          }
        } catch (error) {
          console.error("KPI Fetch Error:", error);
        } finally {
          setFetching(false);
        }
      };
      fetchKpis();
    }
  }, [item]);

  useEffect(() => {
    if (item && item.request_type === "UNIT") {
      const fetchUnits = async () => {
        setFetchingUnits(true);
        try {
          const { isSuccess, data } = await apiCall(
            `${config.ADMIN_API_URL}getUnitMaster`,
            {},
            {},
            "GET"
          );
          if (isSuccess && Array.isArray(data)) {
            setAvailableUnits(data);
          } else if (isSuccess && Array.isArray(data?.data)) {
            setAvailableUnits(data.data);
          }
        } catch (error) {
          console.error("Unit Fetch Error:", error);
        } finally {
          setFetchingUnits(false);
        }
      };
      fetchUnits();
    } else {
      setSelectedUnitId("");
      setAvailableUnits([]);
    }
  }, [item]);

  const filteredUnits = useMemo(() => {
    if (!availableUnits || !Array.isArray(availableUnits)) return [];
    
    // Find the currently selected KPI (question) from the availableKpis list
    const selectedKpiObj = availableKpis.find(kpi => String(kpi.questionId) === String(selectedKpiId));
    if (!selectedKpiObj) return [];
    
    const kpiSupportedUnits = selectedKpiObj.supportedUnits || [];
    const supportedIds = new Set(kpiSupportedUnits.map(u => Number(u.unit_id)));
    
    return availableUnits.filter(u => supportedIds.has(Number(u.id)));
  }, [availableUnits, availableKpis, selectedKpiId]);

  if (!item) return null;

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...styles.modalBox, maxWidth: 500 }}>
        <div style={styles.modalHeader}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#065f46" }}>Approve Request</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Link to Reporting Question</div>
          </div>
          <button onClick={onClose} style={styles.iconBtn}>✕</button>
        </div>
        <div style={{ padding: "16px 24px 20px" }}>
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "12px 16px", marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", textTransform: "uppercase" }}>Request Title</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#14532d" }}>{item.title}</div>
          </div>

          {item?.request_type === "UNIT" ? (
            <div style={{ marginBottom: 14 }}>
              <label style={styles.label}>Linked KPI (Reporting Question)</label>
              <div style={{
                padding: "10px 14px",
                background: "#f8fafc",
                border: "1.5px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#475569"
              }}>
                {item.linked_kpi_title || `KPI (ID: ${item.kpi_id})`}
              </div>
            </div>
          ) : (
            <>
              <label style={styles.label}>Select Related KPI (Reporting Question) {item?.request_type === "KPI" && <span style={{ color: "#ef4444" }}>*</span>}</label>
              <select
                value={selectedKpiId}
                onChange={(e) => setSelectedKpiId(e.target.value)}
                style={{ ...styles.input, appearance: "auto", background: "#fff" }}
                disabled={fetching}
              >
                <option value="">-- Choose from available KPIs --</option>
                {availableKpis.map((kpi) => (
                  <option key={kpi.questionId} value={kpi.questionId}>
                    [{kpi.category}] {kpi.title} (ID: {kpi.questionId})
                  </option>
                ))}
              </select>
              {fetching && <div style={{ fontSize: 11, color: ACCENT, marginTop: 6 }}>Loading database...</div>}
            </>
          )}

          {item?.request_type === "UNIT" && (
            <div style={{ marginTop: 14 }}>
              <label style={styles.label}>Select Unit to Attach <span style={{ color: "#ef4444" }}>*</span></label>
              <select
                value={selectedUnitId}
                onChange={(e) => setSelectedUnitId(e.target.value)}
                style={{ ...styles.input, appearance: "auto", background: "#fff" }}
                disabled={fetchingUnits}
              >
                <option value="">-- Choose Unit --</option>
                {availableUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.unit_name} ({u.unit})
                  </option>
                ))}
              </select>
              {fetchingUnits && <div style={{ fontSize: 11, color: ACCENT, marginTop: 6 }}>Loading units...</div>}
            </div>
          )}
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.btnSecondary} disabled={loading}>Cancel</button>
          <button
            onClick={() => onConfirm(item.id, selectedKpiId, selectedUnitId)}
            disabled={
              loading ||
              (item?.request_type === "KPI" && !selectedKpiId) ||
              (item?.request_type === "UNIT" && !selectedUnitId)
            }
            style={{
              ...styles.btnPrimary,
              background: (
                (item?.request_type === "KPI" && !selectedKpiId) ||
                (item?.request_type === "UNIT" && !selectedUnitId)
              ) ? "#9ca3af" : "#10b981",
              borderColor: (
                (item?.request_type === "KPI" && !selectedKpiId) ||
                (item?.request_type === "UNIT" && !selectedUnitId)
              ) ? "#9ca3af" : "#10b981",
              cursor: (
                (item?.request_type === "KPI" && !selectedKpiId) ||
                (item?.request_type === "UNIT" && !selectedUnitId)
              ) ? "not-allowed" : "pointer"
            }}
          >
            {loading ? <BtnSpinner /> : "Confirm Approval"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   REJECT MODAL
───────────────────────────────────────────────────────── */
const RejectModal = ({ item, onConfirm, onClose, loading }) => {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  if (!item) return null;
  const submit = () => {
    if (!reason.trim()) { setErr("Rejection reason is required."); return; }
    onConfirm(item.id, reason.trim());
  };
  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...styles.modalBox, maxWidth: 480 }}>
        <div style={styles.modalHeader}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#991b1b" }}>Reject Request</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Provide a reason visible to the company</div>
          </div>
          <button onClick={onClose} style={styles.iconBtn}>✕</button>
        </div>
        <div style={{ padding: "16px 24px 20px" }}>
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "12px 16px", marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Request</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#7c2d12" }}>{item.title}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
              <TypeBadge type={item.request_type} />
              <span style={{ fontSize: 12, color: "#9a3412" }}>{item.company_name}</span>
            </div>
          </div>
          <label style={styles.label}>Rejection Reason <span style={{ color: "#ef4444" }}>*</span></label>
          <textarea value={reason} onChange={(e) => { setReason(e.target.value); setErr(""); }}
            placeholder="Explain why this request is being rejected…" rows={4}
            style={{ ...styles.input, height: "auto", resize: "vertical", padding: "10px 12px" }}
          />
          {err && <div style={{ fontSize: 11.5, color: "#ef4444", marginTop: 4 }}>{err}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.btnSecondary} disabled={loading}>Cancel</button>
          <button onClick={submit} disabled={loading} style={{ ...styles.btnPrimary, background: "#ef4444", borderColor: "#ef4444" }}>
            {loading ? <BtnSpinner /> : "✕ Reject Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   DETAIL DRAWER
───────────────────────────────────────────────────────── */
const DetailDrawer = ({ item, onClose, onApprove, onReject }) => {
  if (!item) return null;
  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: 380,
      background: "#fff", zIndex: 1040,
      boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Top strip */}
      <div style={{ height: 4, background: item.status === "APPROVED" ? "#10b981" : item.status === "REJECTED" ? "#ef4444" : "#f59e0b" }} />

      {/* Header */}
      <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid #f0f2f5", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", lineHeight: 1.35, wordBreak: "break-word" }}>{item.title}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
            <TypeBadge type={item.request_type} />
            <StatusBadge status={item.status} />
          </div>
        </div>
        <button onClick={onClose} style={{ ...styles.iconBtn, flexShrink: 0 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
        <DrawerField label="Company" value={item.company_name} accent />
        <DrawerField label="Requested On" value={fmt(item.created_at)} />
        {item.financial_year_id && <DrawerField label="Financial Year ID" value={item.financial_year_id} />}
        {item.actioned_at && <DrawerField label="Actioned On" value={fmt(item.actioned_at)} />}
        {item.kpi_id && <DrawerField label="Linked KPI ID" value={item.kpi_id} accent />}
        {item.unit_id && <DrawerField label="Attached Unit" value={item.linked_unit_name || `Unit #${item.unit_id}`} accent />}

        {item.description && (
          <div style={{ marginTop: 16 }}>
            <div style={styles.drawerLabel}>Description</div>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, background: "#f8fafc", borderRadius: 8, padding: "10px 14px", border: "1px solid #e5e7eb" }}>
              {item.description}
            </div>
          </div>
        )}

        {item.status === "REJECTED" && item.rejection_reason && (
          <div style={{ marginTop: 16, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Rejection Reason</div>
            <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.6 }}>{item.rejection_reason}</div>
          </div>
        )}

        {item.status === "APPROVED" && (
          <div style={{ marginTop: 16, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 13, color: "#065f46", fontWeight: 600, display: "flex", flexDirection: "column", gap: 4 }}>
              <div>✓ Approved</div>
              {item.kpi_id && <div>· Linked KPI ID: <strong>{item.kpi_id}</strong></div>}
              {item.unit_id && <div>· Attached Unit: <strong>{item.linked_unit_name || `Unit #${item.unit_id}`}</strong></div>}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {item.status === "PENDING" && (
        <div style={{ padding: "14px 20px", borderTop: "1px solid #f0f2f5", display: "flex", gap: 10 }}>
          <button onClick={() => onReject(item)} style={{ flex: 1, height: 40, border: "2px solid #ef4444", borderRadius: 8, background: "#fff", color: "#dc2626", fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.15s" }}>
            ✕ Reject
          </button>
          <button onClick={() => onApprove(item)} style={{ flex: 1, height: 40, border: "2px solid #10b981", borderRadius: 8, background: "#fff", color: "#059669", fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.15s" }}>
            ✓ Approve
          </button>
        </div>
      )}
    </div>
  );
};

const DrawerField = ({ label, value, accent }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={styles.drawerLabel}>{label}</div>
    <div style={{ fontSize: 13.5, color: accent ? ACCENT : "#374151", fontWeight: accent ? 600 : 400 }}>{value}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   SPINNER
───────────────────────────────────────────────────────── */
const BtnSpinner = () => (
  <>
    <style>{`@keyframes _kspin { to { transform: rotate(360deg); } }`}</style>
    <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.5)", borderTopColor: "#fff", borderRadius: "50%", animation: "_kspin 0.7s linear infinite", verticalAlign: "middle", marginRight: 4 }} />
  </>
);

const PageSpinner = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "72px 24px", gap: 14 }}>
    <style>{`@keyframes _kspin { to { transform: rotate(360deg); } }`}</style>
    <div style={{ width: 32, height: 32, border: "3px solid #e5e7eb", borderTopColor: ACCENT, borderRadius: "50%", animation: "_kspin 0.8s linear infinite" }} />
    <span style={{ fontSize: 13, color: "#9ca3af" }}>Loading requests…</span>
  </div>
);

/* ─────────────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────────────── */
const Toast = ({ toast }) => {
  if (!toast) return null;
  const ok = toast.type !== "error";
  return (
    <div style={{
      position: "fixed", bottom: 28, right: 28, zIndex: 2000,
      padding: "13px 20px", borderRadius: 10,
      background: ok ? "#ecfdf5" : "#fef2f2",
      border: `1.5px solid ${ok ? "#a7f3d0" : "#fecaca"}`,
      color: ok ? "#065f46" : "#991b1b",
      fontWeight: 600, fontSize: 13,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    }}>
      {toast.msg}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   SHARED STYLES
───────────────────────────────────────────────────────── */
const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
    backdropFilter: "blur(3px)", zIndex: 1050,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  },
  modalBox: {
    background: "#fff", borderRadius: 14, width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "20px 24px 16px", borderBottom: "1px solid #f0f2f5",
  },
  modalFooter: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px", borderTop: "1px solid #f0f2f5", background: "#f9fafb",
  },
  iconBtn: {
    width: 32, height: 32, border: "1.5px solid #e5e7eb", borderRadius: 8,
    background: "#fff", color: "#6b7280", fontSize: 15,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  label: { display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 6, letterSpacing: "0.03em" },
  input: {
    width: "100%", height: 40, padding: "0 12px",
    border: "1.5px solid #d1d5db", borderRadius: 8,
    fontSize: 13, color: "#111827", background: "#f9fafb",
    outline: "none", boxSizing: "border-box", fontFamily: "inherit",
    transition: "border-color 0.15s",
  },
  btnPrimary: {
    height: 38, padding: "0 20px", border: "1.5px solid",
    borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 13,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
    transition: "opacity 0.15s",
  },
  btnSecondary: {
    height: 38, padding: "0 18px", border: "1.5px solid #e5e7eb",
    borderRadius: 8, background: "#fff", color: "#374151",
    fontWeight: 600, fontSize: 13, cursor: "pointer",
  },
  drawerLabel: {
    fontSize: 10, fontWeight: 700, color: "#9ca3af",
    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4,
  },
};

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
const KpiRequest = (props) => {
  const location = useLocation();

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [companyFilter, setCompanyFilter] = useState("ALL"); // NEW

  // Modals / drawer
  const [approveItem, setApproveItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [drawerItem, setDrawerItem] = useState(null);

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => { if (isMounted.current) setToast(null); }, 3200);
  };

  /* ── Fetch ── */
  const fetchRequests = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.ADMIN_API_URL}getAllKpiRequests`, {}, {}, "POST"
      );
      if (isMounted.current) {
        if (isSuccess) setRequests(Array.isArray(data?.data) ? data.data : []);
        else setError("Failed to load requests.");
      }
    } catch { if (isMounted.current) setError("Network error. Please try again."); }
    finally { if (isMounted.current) setIsLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  /* ── Company list for filter ── */
  const companyOptions = useMemo(() => {
    const seen = new Map();
    requests.forEach((r) => {
      if (r.company_id && !seen.has(r.company_id)) {
        seen.set(r.company_id, r.company_name || `Company #${r.company_id}`);
      }
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [requests]);

  /* ── Filtered list ── */
  const filtered = useMemo(() => requests.filter((r) => {
    if (statusFilter !== "ALL" && r.status !== statusFilter) return false;
    if (typeFilter !== "ALL" && r.request_type !== typeFilter) return false;
    if (companyFilter !== "ALL" && String(r.company_id) !== String(companyFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.title.toLowerCase().includes(q) && !(r.company_name || "").toLowerCase().includes(q)) return false;
    }
    return true;
  }), [requests, statusFilter, typeFilter, companyFilter, search]);

  const counts = {
    ALL: requests.length,
    PENDING: requests.filter((r) => r.status === "PENDING").length,
    APPROVED: requests.filter((r) => r.status === "APPROVED").length,
    REJECTED: requests.filter((r) => r.status === "REJECTED").length,
  };

  const hasFilters = statusFilter !== "ALL" || typeFilter !== "ALL" || companyFilter !== "ALL" || search;

  /* ── Actions ── */
  const handleApproveConfirm = async (id, kpiId, unitId) => {
    setActionLoading(true);
    try {
      const rawUser = localStorage.getItem("userID");
      let adminId = (rawUser && rawUser !== "undefined" && rawUser !== "null") ? JSON.parse(rawUser) : null;
      
      if (!adminId) {
        try {
          const rawCurrentUser = localStorage.getItem("currentUser");
          if (rawCurrentUser && rawCurrentUser !== "undefined" && rawCurrentUser !== "null") {
            const currentUser = JSON.parse(rawCurrentUser);
            adminId = currentUser?.data?.user?.dataValues?.id || currentUser?.data?.user_id || null;
          }
        } catch (e) {
          console.error("Failsafe parse of currentUser failed:", e);
        }
      }

      const { isSuccess, data } = await apiCall(
        `${config.ADMIN_API_URL}approveKpiRequest`,
        {},
        {
          id,
          actioned_by: adminId,
          kpi_id: kpiId,
          unit_id: unitId
        },
        "PATCH"
      );
      if (isSuccess) {
        showToast("Request approved and linked successfully");
        setApproveItem(null);
        fetchRequests();
      } else {
        showToast(data?.message || "Failed to approve.", "error");
      }
    } catch (err) {
      console.error("Approval error:", err);
      showToast(err?.message || "Network error.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async (id, reason) => {
    setActionLoading(true);
    try {
      const rawUser = localStorage.getItem("userID");
      let adminId = (rawUser && rawUser !== "undefined" && rawUser !== "null") ? JSON.parse(rawUser) : null;

      if (!adminId) {
        try {
          const rawCurrentUser = localStorage.getItem("currentUser");
          if (rawCurrentUser && rawCurrentUser !== "undefined" && rawCurrentUser !== "null") {
            const currentUser = JSON.parse(rawCurrentUser);
            adminId = currentUser?.data?.user?.dataValues?.id || currentUser?.data?.user_id || null;
          }
        } catch (e) {
          console.error("Failsafe parse of currentUser failed:", e);
        }
      }

      const { isSuccess, data } = await apiCall(
        `${config.ADMIN_API_URL}rejectKpiRequest`, {},
        { id, actioned_by: adminId, rejection_reason: reason }, "PATCH"
      );
      if (isSuccess) { showToast("Request rejected"); setRejectItem(null); setDrawerItem(null); fetchRequests(); }
      else showToast(data?.message || "Failed to reject.", "error");
    } catch (err) {
      console.error("Rejection error:", err);
      showToast(err?.message || "Network error.", "error");
    }
    finally { if (isMounted.current) setActionLoading(false); }
  };

  /* ─────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────── */
  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <style>{`
        @keyframes _kspin { to { transform: rotate(360deg); } }
        .kra-row:hover { background: #f0f7fa !important; }
        .kra-approve-btn:hover { background: #d1fae5 !important; }
        .kra-reject-btn:hover  { background: #fee2e2 !important; }
      `}</style>

      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="color_div_on framwork_2" style={{ padding: "24px 28px" }}>

            {/* ── Page Title ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.03em" }}>
                  KPI Requests
                </h2>
                <p style={{ margin: "5px 0 0", fontSize: 13, color: "#6b7280" }}>
                  Review and manage KPI &amp; Tag requests from companies
                </p>
              </div>
              <button
                onClick={fetchRequests} disabled={isLoading}
                style={{ ...styles.btnSecondary, display: "flex", alignItems: "center", gap: 7, height: 38 }}
              >
                <span style={{ fontSize: 15, display: "inline-block", ...(isLoading ? { animation: "_kspin 1s linear infinite" } : {}) }}>↻</span>
                Refresh
              </button>
            </div>

            {/* ── Stat Cards ── */}
            <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
              <StatCard label="Total Requests" value={counts.ALL} valueColor="#1f2937" bg="#fff" border="#e5e7eb" />
              <StatCard label="Pending" value={counts.PENDING} valueColor="#92400e" bg="#fffbeb" border="#fde68a" />
              <StatCard label="Approved" value={counts.APPROVED} valueColor="#065f46" bg="#ecfdf5" border="#a7f3d0" />
              <StatCard label="Rejected" value={counts.REJECTED} valueColor="#991b1b" bg="#fef2f2" border="#fecaca" />
            </div>

            {/* ── Filter Bar ── */}
            <div style={{
              display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
              padding: "14px 16px", background: "#f8f9fb",
              border: "1.5px solid #e5e7eb", borderRadius: 10, marginBottom: 20,
            }}>
              {/* Search */}
              <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#9ca3af", pointerEvents: "none" }}>🔍</span>
                <input
                  type="text" placeholder="Search by title or company…"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  style={{ ...styles.input, paddingLeft: 34, background: "#fff" }}
                />
              </div>

              {/* Status */}
              <Select value={statusFilter} onChange={setStatusFilter} minWidth={155}>
                <option value="ALL">All Status ({counts.ALL})</option>
                <option value="PENDING">Pending ({counts.PENDING})</option>
                <option value="APPROVED">Approved ({counts.APPROVED})</option>
                <option value="REJECTED">Rejected ({counts.REJECTED})</option>
              </Select>

              {/* Type */}
              <Select value={typeFilter} onChange={setTypeFilter} minWidth={125}>
                <option value="ALL">All Types</option>
                <option value="KPI">KPI</option>
                <option value="TAG">Tag</option>
                <option value="UNIT">Unit</option>
              </Select>

              {/* Company — NEW */}
              <Select value={companyFilter} onChange={setCompanyFilter} minWidth={200}>
                <option value="ALL">All Companies</option>
                {companyOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </Select>

              {hasFilters && (
                <button
                  onClick={() => { setSearch(""); setStatusFilter("ALL"); setTypeFilter("ALL"); setCompanyFilter("ALL"); }}
                  style={{ height: 38, padding: "0 14px", border: "1.5px solid #e5e7eb", borderRadius: 8, background: "#fff", color: "#6b7280", fontSize: 12.5, cursor: "pointer" }}
                >
                  ✕ Clear
                </button>
              )}

              <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>
                {filtered.length} of {requests.length}
              </span>
            </div>

            {/* ── Table ── */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1.5px solid #e5e7eb", overflowX: "auto", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>

              {/* Column headers */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 130px 70px 70px 90px 100px 140px",
                padding: "11px 20px",
                background: "#f4f6f9", borderBottom: "2px solid #e2e6ea",
                minWidth: 850,
              }}>
                {["Title / Description", "Company", "Type", "Unit", "Status", "Requested On", "Actions"].map((h) => (
                  <div key={h} style={{ fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em", padding: "0 4px" }}>
                    {h}
                  </div>
                ))}
              </div>

              {isLoading && <PageSpinner />}

              {!isLoading && error && (
                <div style={{ padding: "52px 24px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#ef4444", marginBottom: 6 }}>Failed to load</div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>{error}</div>
                  <button onClick={fetchRequests} style={{ ...styles.btnPrimary, background: ACCENT, borderColor: ACCENT }}>↻ Retry</button>
                </div>
              )}

              {!isLoading && !error && filtered.length === 0 && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: 10 }}>
                  <span style={{ fontSize: 40, opacity: 0.25 }}>📋</span>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#374151" }}>
                    {requests.length === 0 ? "No requests yet" : "No matching requests"}
                  </div>
                  <div style={{ fontSize: 13, color: "#9ca3af" }}>
                    {requests.length === 0 ? "Companies haven't submitted any requests." : "Try adjusting the filters."}
                  </div>
                </div>
              )}

              {/* ── Rows — each row is a CARD with gap ── */}
              {!isLoading && !error && filtered.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 0, minWidth: 850 }}>
                  {filtered.map((item, idx) => (
                    <div
                      key={item.id}
                      className="kra-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 130px 70px 70px 90px 100px 140px",
                        padding: "14px 20px",
                        background: idx % 2 === 0 ? "#fff" : "#fafbfc",
                        borderBottom: "1px solid #f0f2f5",
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "background 0.12s",
                      }}
                      onClick={() => setDrawerItem(item)}
                    >
                      {/* Title */}
                      <div style={{ padding: "0 4px", minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.title}
                        </div>
                        {item.description && (
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.description}
                          </div>
                        )}
                      </div>

                      {/* Company */}
                      <div style={{ padding: "0 4px", minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: "#374151", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.company_name || `#${item.company_id}`}
                        </div>
                      </div>

                      {/* Type */}
                      <div style={{ padding: "0 4px" }}>
                        <TypeBadge type={item.request_type} />
                      </div>

                      {/* Unit */}
                      <div style={{ padding: "0 4px", fontSize: 12.5, color: "#374151", fontWeight: 600 }}>
                        {item.status === "APPROVED" && item.request_type === "UNIT" && item.unit_id
                          ? (item.linked_unit_name || `Unit #${item.unit_id}`)
                          : "—"}
                      </div>

                      {/* Status */}
                      <div style={{ padding: "0 4px" }}>
                        <StatusBadge status={item.status} />
                      </div>

                      {/* Date */}
                      <div style={{ padding: "0 4px", fontSize: 12.5, color: "#6b7280" }}>
                        {fmt(item.created_at)}
                      </div>

                      {/* Actions */}
                      <div
                        style={{ padding: "0 4px", display: "flex", gap: 8, alignItems: "center" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.status === "PENDING" ? (
                          <>
                            <button
                              className="kra-approve-btn"
                              onClick={() => setApproveItem(item)}
                              style={{ height: 32, padding: "0 12px", border: "1.5px solid #10b981", borderRadius: 7, background: "#ecfdf5", color: "#059669", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "background 0.15s", whiteSpace: "nowrap" }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              className="kra-reject-btn"
                              onClick={() => setRejectItem(item)}
                              style={{ height: 32, padding: "0 12px", border: "1.5px solid #ef4444", borderRadius: 7, background: "#fef2f2", color: "#dc2626", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "background 0.15s", whiteSpace: "nowrap" }}
                            >
                              ✕ Reject
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
                            {item.status === "APPROVED" ? "Approved" : "Rejected"}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer */}
              {!isLoading && !error && filtered.length > 0 && (
                <div style={{
                  padding: "12px 20px", borderTop: "1px solid #f0f2f5",
                  background: "#f8f9fb", display: "flex", justifyContent: "space-between", alignItems: "center",
                  minWidth: 850,
                }}>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    Showing {filtered.length} of {requests.length} requests
                  </span>
                  <div style={{ display: "flex", gap: 16 }}>
                    <span style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>⏳ {counts.PENDING} pending</span>
                    <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>✓ {counts.APPROVED} approved</span>
                    <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>✕ {counts.REJECTED} rejected</span>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── Drawer overlay ── */}
      {drawerItem && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)", zIndex: 1039 }} onClick={() => setDrawerItem(null)} />
          <DetailDrawer
            item={drawerItem} onClose={() => setDrawerItem(null)}
            onApprove={(i) => { setDrawerItem(null); setApproveItem(i); }}
            onReject={(i) => { setDrawerItem(null); setRejectItem(i); }}
          />
        </>
      )}

      <ApproveModal item={approveItem} onConfirm={handleApproveConfirm} onClose={() => setApproveItem(null)} loading={actionLoading} />
      <RejectModal item={rejectItem} onConfirm={handleRejectConfirm} onClose={() => setRejectItem(null)} loading={actionLoading} />
      <Toast toast={toast} />
    </div>
  );
};

export default KpiRequest;